/**
 * Laptop stickers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const laptopStickersPack: ToilePack = {
    slug: 'laptop-stickers',
    category: 'sticker',
    name: 'Laptop stickers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'code', label: 'Code slogan', subject: 'a rounded rectangle lettered "HELLO WORLD" in monospace'},
        {key: 'coffee', label: 'Coffee', subject: 'a takeaway coffee cup with steam'},
        {key: 'plant', label: 'Tiny plant', subject: 'a small potted plant'},
    ],
    styles: [
        {key: 'holo', label: 'Holographic', style: 'holographic sticker, iridescent rainbow sheen across the surface, glossy', palette: 'Iridescent silver shifting through pink, cyan and gold', framing: 'Each sticker isolated with a thick white die-cut border on a plain ground'},
        {key: 'puffy', label: 'Puffy', style: 'puffy 3D sticker, pillowed shape with soft rounded edges, glossy vinyl surface', palette: 'Bright candy colours with a soft highlight', framing: 'Each sticker isolated on a plain ground, its raised edge showing'},
        {key: 'clear', label: 'Clear', style: 'clear sticker, the design printed on transparent film with a white underlayer, the edge visible', palette: 'Bright flat colour with a visible clear margin', framing: 'Each sticker isolated on a pale ground with the transparent edge showing'},
    ],
    directions: ['a terminal window instead', 'a whole sticker sheet', 'in monochrome colors'],
    directionHint: 'try: a terminal window instead',
    seo: {
        title: 'Laptop Stickers: Code Slogan, Coffee, Plant',
        metaDescription: 'Nine laptop stickers — a lettered code slogan, a coffee cup, a tiny plant — in holographic, puffy, and clear finishes.',
        h1: 'Laptop stickers: code slogan, coffee, and plant in three finishes',
        tagline: 'Nine laptop stickers, three designs, three finishes each.',
        intro: 'The sticker for the lid: a lettered code slogan, a coffee cup, a tiny plant. Each rendered holographic, puffy, and clear — the finishes that actually read well on a MacBook lid. Ask for a different slogan, language, or desk object, and the same three finishes carry it.',
        useCases: [
            'decorating a MacBook or laptop lid',
            'a developer or programmer sticker pack',
            'a coffee-and-code aesthetic sticker set',
            'a work-from-home desk sticker',
            'a gift for a coder or student',
        ],
        faq: [
            {q: 'Will these show up well on a silver or space-gray laptop?', a: 'Clear and holographic finishes work on any lid color; a puffy finish prints best against a plain or light background.'},
            {q: 'Can I get a different coding language or a specific joke?', a: 'Yes — the direction field takes any slogan, language reference, or in-joke, rendered in the same three finishes.'},
            {q: "What's the difference between holographic, puffy, and clear?", a: 'Holographic is a rainbow-sheen finish, puffy is a domed 3D look, and clear is a transparent background — match whichever your sticker paper supports.'},
            {q: 'Are these full-size laptop skins?', a: 'No — these are individual stickers meant for the lid or trackpad area, not a full-coverage skin.'},
            {q: 'Can I print these for a hackathon or team giveaway?', a: 'Yes, for personal and small-group use; check the license terms before reselling the designs.'},
        ],
    },
}
